import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';

import {
  AdminDashboardService,
  AdminDashboardFilter,
  AdminMemberRow,
  AdminFilterOptions,
} from '../../services/admin-dashboard';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { CoiDownloader } from '../../services/coi-downloader';
import { HospicashLetterService } from '../../services/hospicash-letter';
import { IndDatePipe } from '../../pipes/ind-date';
import { UnissuedPanel } from '../../components/unissued-panel/unissued-panel';
import { PolicyIssueService } from '../../services/policy-issue';

type Severity = 'success' | 'danger' | 'warn' | 'info' | 'secondary';

/**
 * All Members — the admin master list over every insured member (partner- and
 * client-sourced). Backed by GET /api/AdminDashboard/Members: server-side
 * paging + search + filters, [Authorize(Roles="Admin")] on the backend.
 */
@Component({
  selector: 'app-members',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    TagModule,
    ButtonModule,
    SelectModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    MenuModule,
    IndDatePipe,
    UnissuedPanel,
  ],
  templateUrl: './members.html',
  styleUrls: ['./members.css'],
})
export class MembersPage implements OnInit {
  private svc = inject(AdminDashboardService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private cois = inject(CoiDownloader);
  private letters = inject(HospicashLetterService);
  private issuer = inject(PolicyIssueService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  loading = signal(false);
  rows = signal<AdminMemberRow[]>([]);
  /** member id whose certificate is being generated (button spinner). */
  generatingId = signal<number | null>(null);
  /** member id whose welcome letter is being generated (button spinner). */
  letteringId = signal<number | null>(null);
  /** items for the shared per-row "Documents" popup menu (rebuilt on open). */
  docItems = signal<MenuItem[]>([]);
  total = signal(0);
  page = signal(1);
  pageSize = signal(10);

  search = signal('');
  fPartner = signal<number | null>(null);
  fClient = signal<number | null>(null);
  fSource = signal<string | null>(null);
  fPay = signal<string | null>(null);
  fKyc = signal<string | null>(null);

  options = signal<AdminFilterOptions | null>(null);
  private searchTimer: any = null;

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
    this.svc.filterOptions().subscribe({
      next: (res: any) => {
        if (res?.success !== false) this.options.set(res?.data ?? null);
      },
    });
  }

  private filter(): AdminDashboardFilter {
    return {
      partnerId: this.fPartner() ?? undefined,
      clientId: this.fClient() ?? undefined,
      source: this.fSource() ?? undefined,
      paymentStatus: this.fPay() ?? undefined,
      kycStatus: this.fKyc() ?? undefined,
      search: this.search().trim() || undefined,
      page: this.page(),
      pageSize: this.pageSize(),
    };
  }

  load() {
    this.loading.set(true);
    this.svc.members(this.filter()).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to load members');
          return;
        }
        this.rows.set(res?.data?.items ?? []);
        this.total.set(res?.data?.totalCount ?? 0);
      },
      error: () => this.loading.set(false),
    });
  }

  onLazy(e: any) {
    const size = e?.rows ?? this.pageSize();
    this.page.set(Math.floor((e?.first ?? 0) / size) + 1);
    this.pageSize.set(size);
    this.load();
  }

  onSearch(v: string) {
    this.search.set(v);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page.set(1);
      this.load();
    }, 350);
  }

  onFilter() {
    this.page.set(1);
    this.load();
  }

  resetFilters() {
    this.fPartner.set(null);
    this.fClient.set(null);
    this.fSource.set(null);
    this.fPay.set(null);
    this.fKyc.set(null);
    this.search.set('');
    this.page.set(1);
    this.load();
  }

  // ---- dropdown sources ----
  partnerOptions() {
    return (this.options()?.partners ?? []).map((p) => ({ label: p.name, value: p.id }));
  }
  clientOptions() {
    return (this.options()?.clients ?? []).map((c) => ({ label: c.name, value: c.id }));
  }
  sourceOptions() {
    return (this.options()?.sources ?? []).map((s) => ({ label: this.title(s), value: s }));
  }
  payOptions() {
    return (this.options()?.memberPaymentStates ?? []).map((s) => ({ label: this.title(s), value: s }));
  }
  kycOptions() {
    return (this.options()?.kycStatuses ?? []).map((s) => ({ label: this.title(s), value: s }));
  }

  private title(code: string): string {
    return code
      .toLowerCase()
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  // ---- display helpers ----
  paySeverity(s?: string | null): Severity {
    switch ((s || '').toUpperCase()) {
      case 'PAID': return 'success';
      case 'REFUNDED': return 'info';
      case 'UNPAID': return 'warn';
      default: return 'secondary';
    }
  }

  kycSeverity(s?: string | null): Severity {
    switch ((s || '').toUpperCase()) {
      case 'VERIFIED': return 'success';
      case 'PENDING': return 'warn';
      case 'NOT_VERIFIED': return 'danger';
      default: return 'secondary';
    }
  }

  sourceOf(r: AdminMemberRow): { kind: string; name: string } {
    if ((r.source || '').toUpperCase() === 'CLIENT' || r.clientName) {
      return { kind: 'Client', name: r.clientName || '—' };
    }
    return { kind: 'Partner', name: r.partnerName || '—' };
  }

  inr(v?: number | null): string {
    if (v == null) return '—';
    return '₹' + Number(v).toLocaleString('en-IN');
  }

  // ---- row actions ----
  openLookup(r: AdminMemberRow) {
    this.router.navigate(['/member-lookup'], {
      queryParams: { q: r.memberUniqueId || String(r.id) },
    });
  }

  openPayments(r: AdminMemberRow) {
    this.router.navigate(['/payments/list'], {
      queryParams: { q: r.memberUniqueId || r.fullName || String(r.id) },
    });
  }

  openCertificate(r: AdminMemberRow) {
    this.router.navigate(['/policies/certificate', r.id]);
  }

  /** Whether this member is paid/issued (a certificate can be generated). */
  canGenerateCoi(r: AdminMemberRow): boolean {
    return (r.paymentStatus || '').toUpperCase() === 'PAID' || !!r.coiGenerated;
  }

  /**
   * Generate the certificate now and download it. The main app renders on the fly, so this both
   * produces and downloads the PDF. If the member's client has no COI header yet, the backend
   * blocks it and the downloader surfaces that message (create the header first).
   */
  async generateCoi(r: AdminMemberRow) {
    if (this.generatingId() !== null) return;
    this.generatingId.set(r.id);
    try {
      await this.cois.downloadCoi(r.id, r.policyNo ?? undefined);
    } finally {
      this.generatingId.set(null);
    }
  }

  /** Partner-onboarded + issued members can get the Hospicash welcome letter (main app only). */
  canWelcomeLetter(r: AdminMemberRow): boolean {
    return (
      this.sourceOf(r).kind === 'Partner' &&
      ((r.paymentStatus || '').toUpperCase() === 'PAID' || !!r.coiGenerated)
    );
  }

  async downloadLetter(r: AdminMemberRow) {
    if (this.letteringId() !== null) return;
    this.letteringId.set(r.id);
    try {
      await this.letters.download(r.id, r.policyNo ?? undefined);
    } finally {
      this.letteringId.set(null);
    }
  }

  // ---- stranded members (paid, never issued) ----
  /**
   * PAID with no policy number. The money went through and nothing was minted — usually a master
   * policy whose number series ran out — so the customer is waiting for a certificate that was
   * never produced. Worth calling out on the row itself, not only in the panel above the table.
   */
  isStranded(r: AdminMemberRow): boolean {
    return (r.paymentStatus || '').toUpperCase() === 'PAID' && !r.policyNo;
  }

  /** Only a verified member can be issued; the rest need OTP verification first. */
  canIssue(r: AdminMemberRow): boolean {
    return this.isStranded(r) && (r.kycStatus || '').toUpperCase() === 'VERIFIED';
  }

  issuePolicy(r: AdminMemberRow) {
    if (!this.canIssue(r) || this.issuer.issuing() !== null) return;

    this.issuer.issuing.set(r.id);
    this.issuer.reissue(r.id).subscribe({
      next: (res: any) => {
        this.issuer.issuing.set(null);
        if (res?.success === false) {
          this.toast.error(res?.message || 'The policy could not be issued.');
          return;
        }
        this.toast.success(res?.message || 'Policy issued.', 'Issued');
        this.issuer.remove(r.id);
        this.load();
      },
      error: () => {
        this.issuer.issuing.set(null);
        this.toast.error('The policy could not be issued. Please try again.');
      },
    });
  }

  issuingId() {
    return this.issuer.issuing();
  }

  // ---- documents popup menu ----
  /** Any downloadable document for this member? (certificate and/or welcome letter). */
  hasDocs(r: AdminMemberRow): boolean {
    return this.canGenerateCoi(r) || !!r.coiGenerated || this.canWelcomeLetter(r);
  }

  /** Is a document currently being generated for this row (button spinner)? */
  docBusy(r: AdminMemberRow): boolean {
    return this.generatingId() === r.id || this.letteringId() === r.id;
  }

  /** Build the per-row document menu on open, then toggle the shared popup. */
  openDocMenu(r: AdminMemberRow, event: Event, menu: { toggle: (e: Event) => void }) {
    const isPartner = this.sourceOf(r).kind === 'Partner';
    const items: MenuItem[] = [];

    if (r.coiGenerated) {
      items.push({ label: 'View certificate', icon: 'pi pi-id-card', command: () => this.openCertificate(r) });
    }
    if (this.canGenerateCoi(r)) {
      items.push({
        // For partner members the certificate PDF is prefixed with the welcome letter (one file).
        label: isPartner ? 'Download welcome letter + certificate' : 'Download certificate (PDF)',
        icon: 'pi pi-file-pdf',
        command: () => this.generateCoi(r),
      });
    }
    if (this.canWelcomeLetter(r)) {
      items.push({ label: 'Download welcome letter only', icon: 'pi pi-envelope', command: () => this.downloadLetter(r) });
    }

    this.docItems.set(items);
    menu.toggle(event);
  }
}
